/**
 * 阅读一文多题：按 passageNumber + passage 文本聚簇抽样。
 * Training / Mock 组卷共用，避免同文题被 shuffle 打散成「半套」。
 */

export type PassageClusterable = {
  id: string;
  passage?: string | null;
  /** 共享材料集；优先于 passageNumber 指纹 */
  passageSetId?: unknown;
  /** 考试编排字段；不在核心 Question 接口上声明，松散读取 */
  passageNumber?: unknown;
};

function normalizePassageFingerprint(passage: string): string {
  return passage.trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * 同文簇键：优先 passageSetId；否则 passageNumber+材料指纹；再否则材料前缀 / 单题。
 */
export function passageClusterKey(question: PassageClusterable): string {
  const setId =
    typeof question.passageSetId === 'string' ? question.passageSetId.trim() : '';
  if (setId) return `ps:${setId}`;

  const passage = normalizePassageFingerprint(String(question.passage ?? ''));
  const pn = Number(question.passageNumber);
  const hasPn = Number.isFinite(pn) && pn > 0;

  if (hasPn && passage.length >= 20) {
    return `pn:${Math.trunc(pn)}|${passage.slice(0, 160)}`;
  }
  if (passage.length >= 40) {
    return `p:${passage.slice(0, 160)}`;
  }
  return `id:${question.id}`;
}

/**
 * 选中若干题 index 后，扩成同文整簇（导入/丢弃/绿区门闸共用）。
 * 无共享材料的题保持单题簇。
 */
export function expandIndexesByPassageCluster(
  questions: PassageClusterable[],
  seedIndexes: Iterable<number>
): number[] {
  const seeds = [...new Set([...seedIndexes].filter((i) => Number.isInteger(i) && i >= 0))];
  if (seeds.length === 0 || questions.length === 0) return [];

  const indexesByKey = new Map<string, number[]>();
  for (let i = 0; i < questions.length; i += 1) {
    const q = questions[i];
    if (!q) continue;
    const key = passageClusterKey({ ...q, id: q.id || `idx:${i}` });
    const bucket = indexesByKey.get(key);
    if (bucket) bucket.push(i);
    else indexesByKey.set(key, [i]);
  }

  const out = new Set<number>();
  for (const seed of seeds) {
    if (seed >= questions.length) continue;
    const q = questions[seed];
    if (!q) {
      out.add(seed);
      continue;
    }
    const key = passageClusterKey({ ...q, id: q.id || `idx:${seed}` });
    const cluster = indexesByKey.get(key) ?? [seed];
    for (const index of cluster) out.add(index);
  }

  return [...out].sort((a, b) => a - b);
}

/**
 * 按簇分组 index 列表。
 */
export function groupIndexesByPassageCluster(
  questions: PassageClusterable[],
  indexes: Iterable<number>
): Map<string, number[]> {
  const groups = new Map<string, number[]>();
  for (const index of indexes) {
    if (!Number.isInteger(index) || index < 0 || index >= questions.length) continue;
    const q = questions[index];
    if (!q) continue;
    const key = passageClusterKey({ ...q, id: q.id || `idx:${index}` });
    const bucket = groups.get(key);
    if (bucket) bucket.push(index);
    else groups.set(key, [index]);
  }
  for (const [, bucket] of groups) bucket.sort((a, b) => a - b);
  return groups;
}

function defaultShuffle<T>(array: T[]): T[] {
  const out = [...array];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * 尽量整簇纳入：在能放进剩余名额的簇里优先取最大簇；
 * 没有任何整簇可放时，才从最大剩余簇取前缀凑满。
 */
export function samplePreferringPassageClusters<T extends PassageClusterable>(
  candidates: T[],
  limit: number,
  shuffleFn: <U>(items: U[]) => U[] = defaultShuffle
): T[] {
  if (limit <= 0 || candidates.length === 0) return [];

  const byKey = new Map<string, T[]>();
  for (const q of shuffleFn(candidates)) {
    const key = passageClusterKey(q);
    const bucket = byKey.get(key);
    if (bucket) bucket.push(q);
    else byKey.set(key, [q]);
  }

  for (const [key, bucket] of byKey) {
    byKey.set(key, shuffleFn(bucket));
  }

  if (candidates.length <= limit) {
    const orderedKeys = shuffleFn([...byKey.keys()]);
    const all: T[] = [];
    for (const key of orderedKeys) {
      all.push(...(byKey.get(key) ?? []));
    }
    return all;
  }

  const selected: T[] = [];

  while (selected.length < limit && byKey.size > 0) {
    const room = limit - selected.length;
    const entries = [...byKey.entries()].map(([key, cluster]) => ({
      key,
      size: cluster.length,
      cluster,
    }));

    const fitting = entries.filter((e) => e.size <= room).sort((a, b) => b.size - a.size);
    if (fitting.length > 0) {
      // 同尺寸时打散，避免永远偏向某一键
      const bestSize = fitting[0]!.size;
      const best = shuffleFn(fitting.filter((e) => e.size === bestSize));
      const pick = best[0]!;
      selected.push(...pick.cluster);
      byKey.delete(pick.key);
      continue;
    }

    const largest = [...entries].sort((a, b) => b.size - a.size);
    const topSize = largest[0]!.size;
    const top = shuffleFn(largest.filter((e) => e.size === topSize));
    const pick = top[0]!;
    selected.push(...pick.cluster.slice(0, room));
    break;
  }

  return selected;
}
